<script>
import baseMixin from '@/mixins/baseMixin'
import { mapGetters } from 'vuex'
import {
  BookingStatus,
  hasCandidateModifiedBookingOrStatedDidNotHappen,
} from '@/shared/constants/booking/BookingStatus'
import FixedActionFooter from '../layout/fixed-action-footer.vue'

export default {
  name: 'TimesheetSelectionActionBar',
  components: { FixedActionFooter },
  mixins: [baseMixin],
  props: {
    selectedTimesheets: {
      type: Array,
      default: () => [],
    },
    approvedForInvoiceCount: {
      type: Number,
      default: 0,
    },
    pendingReviewCount: {
      type: Number,
      default: 0,
    },
  },
  computed: {
    ...mapGetters({
      isLoading: 'timesheets/isLoading',
      isLoadingCRUD: 'timesheets/isLoadingCRUD',
    }),
    selectedCount() {
      return this.selectedTimesheets.length || 0
    },
    hasSelection() {
      return this.selectedCount > 0
    },
    unmodifiedCount() {
      if (!this.hasSelection) return 0

      return this.selectedTimesheets.filter(
        (timesheet) =>
          !hasCandidateModifiedBookingOrStatedDidNotHappen(
            timesheet.bookingStatus
          )
      ).length
    },
    candidateModifiedCount() {
      if (!this.hasSelection) return 0

      return this.selectedTimesheets.filter(
        (timesheet) =>
          timesheet.bookingStatus ===
          BookingStatus.CANDIDATE_SUBMITTED_TIMESHEET_MODIFIED
      ).length
    },
    candidateDidNotHappenCount() {
      if (!this.hasSelection) return 0

      return this.selectedTimesheets.filter(
        (timesheet) =>
          timesheet.bookingStatus ===
          BookingStatus.CANDIDATE_SUBMITTED_TIMESHEET_DID_NOT_HAPPEN
      ).length
    },
    hasUnmodified() {
      return this.unmodifiedCount > 0
    },
    hasModified() {
      return this.candidateModifiedCount > 0
    },
    hasRemoved() {
      return this.candidateDidNotHappenCount > 0
    },
    hasMixedSelection() {
      if (!this.hasSelection) return false

      let mixedSelectionTypeCount = this.hasUnmodified ? 1 : 0
      mixedSelectionTypeCount += this.hasModified ? 1 : 0
      mixedSelectionTypeCount += this.hasRemoved ? 1 : 0

      return mixedSelectionTypeCount > 1
    },
    compSelectionText() {
      if (this.hasMixedSelection) {
        return this.$t(
          'timesheets.selectionToolbar.selectionStatus.mixedSelection'
        )
      }

      if (this.hasUnmodified) {
        return this.$tc(
          'timesheets.selectionToolbar.selectionStatus.unmodifiedSelection',
          this.selectedCount,
          [this.selectedCount]
        )
      }

      if (this.hasModified) {
        return this.$tc(
          'timesheets.selectionToolbar.selectionStatus.modifiedSelection',
          this.selectedCount,
          [this.selectedCount]
        )
      }

      if (this.hasRemoved) {
        return this.$tc(
          'timesheets.selectionToolbar.selectionStatus.didNotHappenSelection',
          this.selectedCount,
          [this.selectedCount]
        )
      }

      return ''
    },
    submissionBreakdownText() {
      return `(${
        this.hasUnmodified
          ? `${this.$t(
              'timesheets.selectionToolbar.selectionStatus.subtitle.unmodifiedCount',
              [this.unmodifiedCount]
            )} `
          : ''
      }${
        this.hasModified
          ? `${this.$t(
              'timesheets.selectionToolbar.selectionStatus.subtitle.modifiedCount',
              [this.candidateModifiedCount]
            )}${this.hasRemoved ? ', ' : ''}`
          : ''
      }${
        this.hasRemoved
          ? this.$t(
              'timesheets.selectionToolbar.selectionStatus.subtitle.didNotHappenCount',
              [this.candidateDidNotHappenCount]
            )
          : ''
      })`
    },
  },
}
</script>

<template>
  <FixedActionFooter>
    <transition name="slide-fade" mode="out-in">
      <div
        v-if="hasSelection"
        key="has-selection"
        data-testid="timesheet-selection-action-bar-has-selection"
        class="ml-auto d-flex align-center"
      >
        <div class="blue--text mr-4 text-right">
          <p :class="hasMixedSelection ? 'mb-n2' : 'mb-0'">{{
            compSelectionText
          }}</p>
          <span v-if="hasMixedSelection" class="text-caption">{{
            submissionBreakdownText
          }}</span>
        </div>
        <v-btn
          :disabled="!hasSelection || hasMixedSelection"
          data-testid="timesheet-selection-action-bar-submit-btn"
          color="primary"
          class="custom-elevation px-8"
          :loading="isLoadingCRUD"
          @click="$emit('submitTimesheets')"
        >
          {{ $t('general.submit') }}
        </v-btn>
      </div>
      <div
        v-else-if="!isLoading"
        key="no-selection"
        data-testid="timesheet-selection-action-bar-no-selection"
        class="ml-auto d-flex align-center"
      >
        <span
          class="purple--text mr-4"
          data-testid="timesheet-selection-action-bar-invoiced-count"
        >
          {{ approvedForInvoiceCount }}
          {{ $t('timesheets.timesheetApprovedCardTitle') }}
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon small class="purple--text" v-bind="attrs" v-on="on">
                mdi-help-circle
              </v-icon>
            </template>
            <span>
              {{ $t('timesheets.timesheetApprovedTooltipText') }}
            </span>
          </v-tooltip>
        </span>

        <span
          class="blue--text"
          data-testid="timesheet-selection-action-bar-pending-review-count"
        >
          {{ pendingReviewCount }}
          {{ $t('timesheets.timesheetPendingReviewCardTitle') }}
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon small class="blue--text" v-bind="attrs" v-on="on">
                mdi-help-circle
              </v-icon>
            </template>
            <span>
              {{ $t('timesheets.timesheetPendingReviewTooltipText') }}
            </span>
          </v-tooltip>
        </span>
      </div>
    </transition>
  </FixedActionFooter>
</template>

<style lang="scss" scoped>
@import '@styles';

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.1s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
</style>
